import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage, extractFieldErrors } from '@/shared/api/errors';
import {
  apiAdminOwnerlessCheck,
  apiParcelsAdminPost,
} from '@/entities/parcels';
import { apiParcelsProductsPost } from '@/entities/parcels-products';
import {
  FeaturesParcelFormSectionAddress,
  FeaturesParcelFormSectionAddToWarehouse,
  FeaturesParcelFormSectionBasic,
  FeaturesParcelFormSectionCargoId,
  FeaturesParcelFormSectionConfirmedShipping,
  FeaturesParcelFormSectionPersonal,
  adminParcelCreateSchema,
  emptyAdminParcelCreateValues,
  type AdminParcelCreateFormValues,
  type AdminParcelFormValues,
  type AddToWarehouseDisabledReason,
  type ConfirmedShippingDisabledReason,
} from '@/features/parcel-form';
import {
  FeaturesParcelCreateInlineProducts,
  validateDraft,
  revalidateDraftErrors,
  type ParcelCreateProductDraft,
  type ParcelCreateProductDraftErrors,
} from '@/features/parcel-create-products';
import type {
  ApiParcelsAdminPostBody,
  ApiParcelsGetItem,
} from '@/shared/types/api/parcels';
import type { ApiParcelsProductsPostBody } from '@/shared/types/api/parcels-products';

const toBody = (
  v: AdminParcelCreateFormValues,
  effectivelyInWarehouse: boolean,
): ApiParcelsAdminPostBody => ({
  cargo_id: v.cargo_id,
  name: v.name,
  trek_id: v.trek_id,
  store_name: v.store_name || undefined,
  address_name: v.address_name,
  region: v.region,
  city: v.city,
  street: v.street,
  building: v.building,
  apartment: v.apartment || undefined,
  additional_info: v.additional_info || undefined,
  first_name: v.first_name,
  last_name: v.last_name,
  middle_name: v.middle_name,
  phone_number: v.phone_number,
  passport_series: v.passport_series,
  pinfl: v.pinfl,
  // Only send the flag when the operator actually ticked it AND the
  // parcel will effectively be in the warehouse — either the probe found
  // an OwnerlessProduct or the operator asked us to auto-create one via
  // ``add_to_warehouse``. The backend re-checks but front-loading this
  // avoids the round-trip+rollback.
  confirmed_for_shipping:
    effectivelyInWarehouse && v.confirmed_for_shipping ? true : undefined,
  add_to_warehouse: v.add_to_warehouse ? true : undefined,
});

export const WidgetsParcelCreateMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const form = useForm<AdminParcelCreateFormValues>({
    resolver: zodResolver(adminParcelCreateSchema),
    defaultValues: emptyAdminParcelCreateValues,
    mode: 'onBlur',
  });
  // Base sections only read/write the shared parcel fields.
  const baseForm = form as unknown as UseFormReturn<AdminParcelFormValues>;

  // Inline declaration drafts. ``rowErrors`` is keyed by ``client_id`` and
  // only populated after the operator has tried to submit at least once,
  // so we don't flash red on every keystroke.
  const [drafts, setDrafts] = useState<ParcelCreateProductDraft[]>([]);
  const [rowErrors, setRowErrors] = useState<
    Record<string, ParcelCreateProductDraftErrors>
  >({});
  // Wraps the inline-products Section so we can scroll to it when the
  // submit-time "at least one product" guard fires (owner spec 2026-06-02).
  const productsSectionRef = useRef<HTMLDivElement | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);

  // Warehouse-probe state for the "Joʻnatishga tasdiqlangan" checkbox.
  // ``warehouseExists`` is tri-valued: ``null`` -> not yet checked,
  // ``true`` / ``false`` -> resolved. We only enable the checkbox on
  // ``true``; anything else disables it.
  const [warehouseExists, setWarehouseExists] = useState<boolean | null>(null);
  const [probing, setProbing] = useState(false);
  // Guard against stale probe resolutions (operator edits trek_id while
  // an older fetch is still in flight).
  const probeSeqRef = useRef(0);

  const runProbe = useCallback(
    async (trekId: string) => {
      const trimmed = trekId.trim();
      if (!trimmed) {
        setProbing(false);
        setWarehouseExists(null);
        return;
      }
      const seq = ++probeSeqRef.current;
      setProbing(true);
      try {
        const res = await apiAdminOwnerlessCheck(trimmed);
        if (probeSeqRef.current !== seq) return;
        setWarehouseExists(res.exists);
        // Stale-state guard: if the warehouse already holds the box we
        // don't need ``add_to_warehouse`` — clear it so the operator can't
        // double-tick. Confirm stays as the operator left it.
        if (res.exists) {
          form.setValue('add_to_warehouse', false, { shouldDirty: false });
        } else {
          // Not in warehouse: a stale confirm tick can't pass unless the
          // operator also ticked ``add_to_warehouse`` (handled below).
          if (!form.getValues('add_to_warehouse')) {
            form.setValue('confirmed_for_shipping', false, { shouldDirty: false });
          }
        }
      } catch {
        if (probeSeqRef.current !== seq) return;
        // Treat probe errors as "not in warehouse" — safer default for
        // the confirmed-shipping checkbox.
        setWarehouseExists(false);
        if (!form.getValues('add_to_warehouse')) {
          form.setValue('confirmed_for_shipping', false, { shouldDirty: false });
        }
      } finally {
        if (probeSeqRef.current === seq) setProbing(false);
      }
    },
    [form],
  );

  const onTrekIdBlur = useCallback(
    (value: string) => {
      void runProbe(value);
    },
    [runProbe],
  );

  const onTrekIdChange = useCallback(
    (_value: string) => {
      // Any change invalidates a prior warehouse decision and clears both
      // checkboxes so a stale tick can't sneak through.
      probeSeqRef.current++;
      setProbing(false);
      setWarehouseExists(null);
      form.setValue('confirmed_for_shipping', false, { shouldDirty: false });
      form.setValue('add_to_warehouse', false, { shouldDirty: false });
    },
    [form],
  );

  const trekIdValue = form.watch('trek_id') ?? '';
  const addToWarehouse = form.watch('add_to_warehouse') ?? false;
  const confirmedForShipping = form.watch('confirmed_for_shipping') ?? false;

  // Owner spec: confirm checkbox unticks + re-disables the moment
  // ``add_to_warehouse`` goes from on to off (if the warehouse doesn't
  // physically have it). Cheaper than recomputing in every render.
  useEffect(() => {
    if (!addToWarehouse && warehouseExists !== true && confirmedForShipping) {
      form.setValue('confirmed_for_shipping', false, { shouldDirty: false });
    }
  }, [addToWarehouse, warehouseExists, confirmedForShipping, form]);

  // Effective "is in warehouse at submit time" — used to gate both
  // ``confirmed_for_shipping`` enablement and the body field above.
  const effectivelyInWarehouse = warehouseExists === true || addToWarehouse;

  const confirmDisabledReason = useMemo<ConfirmedShippingDisabledReason>(() => {
    if (!trekIdValue.trim()) return 'no-trek-id';
    if (!effectivelyInWarehouse) return 'not-in-warehouse';
    return null;
  }, [trekIdValue, effectivelyInWarehouse]);

  const addToWarehouseDisabledReason =
    useMemo<AddToWarehouseDisabledReason>(() => {
      if (!trekIdValue.trim()) return 'no-trek-id';
      if (warehouseExists === true) return 'already-in-warehouse';
      return null;
    }, [trekIdValue, warehouseExists]);

  const postDrafts = useCallback(
    async (parcelId: string): Promise<{ failed: number }> => {
      // Validate first so per-row errors surface before we hit the API.
      const newErrors: Record<string, ParcelCreateProductDraftErrors> = {};
      const valid: { client_id: string; body: ApiParcelsProductsPostBody }[] = [];
      for (const draft of drafts) {
        const result = validateDraft(draft);
        if (result.ok) {
          valid.push({ client_id: draft.client_id, body: result.body });
        } else {
          newErrors[draft.client_id] = result.errors;
        }
      }
      setRowErrors(newErrors);
      let failed = Object.keys(newErrors).length;
      for (const row of valid) {
        try {
          await apiParcelsProductsPost(parcelId, row.body);
        } catch (err) {
          failed += 1;
          toast.error(extractErrorMessage(err));
        }
      }
      return { failed };
    },
    [drafts, toast],
  );

  const create = useMutate({
    api: (body: ApiParcelsAdminPostBody) => apiParcelsAdminPost(body),
    invalidateKeys: [queryKeys.parcels.all, queryKeys.pinfl.all],
    onSuccess: async (parcel: ApiParcelsGetItem) => {
      toast.success(t('parcels.toast.created'));
      const { failed } = await postDrafts(parcel.id);
      if (failed > 0) {
        toast.error(t('parcels.products.toast.partialFailure', { count: failed }));
      }
      navigate('/parcels');
    },
    onError: (err, message) => {
      const fieldErrors = extractFieldErrors(err);
      Object.entries(fieldErrors).forEach(([field, msg]) => {
        if (field in emptyAdminParcelCreateValues) {
          form.setError(field as keyof AdminParcelCreateFormValues, {
            type: 'server',
            message: msg,
          });
        }
      });
      toast.error(message);
    },
  });

  const onSubmit = form.handleSubmit((v) => {
    // Owner spec (2026-06-02): when "Joʻnatishga tasdiqlash" is ticked the
    // parcel skips IN_PROGRESS and lands in SORTING, which means the
    // declaration must already be present. Refuse to submit if there isn't
    // at least one valid inline product row, and surface the error both as
    // a toast and as a per-section message so the operator can find the
    // missing block on a long form. (No rollback semantics here — we
    // never POST the parcel in this branch.)
    if (v.confirmed_for_shipping) {
      const anyValid = drafts.some((d) => validateDraft(d).ok);
      if (!anyValid) {
        const msg = t('parcels.form.errors.confirmRequiresProducts');
        setProductsError(msg);
        toast.error(msg);
        productsSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }
    }
    setProductsError(null);
    // Final sanity-check on the inline drafts before we POST the parcel.
    // We surface validation errors but still proceed to create the parcel
    // — the drafts loop runs in ``onSuccess`` and any row that doesn't
    // validate just gets skipped (toast'd) per the spec's "best-effort"
    // rollback policy.
    const previewErrors: Record<string, ParcelCreateProductDraftErrors> = {};
    for (const d of drafts) {
      const res = validateDraft(d);
      if (!res.ok) previewErrors[d.client_id] = res.errors;
    }
    setRowErrors(previewErrors);
    if (Object.keys(previewErrors).length > 0) {
      toast.error(t('parcels.products.toast.fixRows'));
      return;
    }
    create.mutate(toBody(v, effectivelyInWarehouse));
  });

  return (
    <>
      <PageHeader title={t('parcels.create.title')} subtitle={t('parcels.create.subtitle')} showBack />
      <form
        onSubmit={onSubmit}
        className="space-y-4 px-4 py-4 lg:px-6 lg:py-6"
        data-cy="parcel-create-form"
      >
        <FeaturesParcelFormSectionCargoId form={form} />
        <FeaturesParcelFormSectionBasic
          form={baseForm}
          onTrekIdBlur={onTrekIdBlur}
          onTrekIdChange={onTrekIdChange}
        />
        <FeaturesParcelFormSectionAddToWarehouse
          form={form}
          disabledReason={addToWarehouseDisabledReason}
          loading={probing}
        />
        <FeaturesParcelFormSectionConfirmedShipping
          form={form}
          disabledReason={confirmDisabledReason}
          loading={probing}
        />
        <FeaturesParcelFormSectionAddress form={baseForm} />
        <FeaturesParcelFormSectionPersonal form={baseForm} />

        <div ref={productsSectionRef}>
          <FeaturesParcelCreateInlineProducts
            drafts={drafts}
            errors={rowErrors}
            onChange={(next) => {
              setDrafts(next);
              setRowErrors((prev) => revalidateDraftErrors(prev, next));
              if (productsError) setProductsError(null);
            }}
          />
          {productsError && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {productsError}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          <div className="mx-auto flex max-w-3xl gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={create.isPending}
              block
            >
              {t('parcels.create.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} block>
              {t('parcels.create.save')}
            </Button>
          </div>
          {create.error && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {extractErrorMessage(create.error)}
            </p>
          )}
        </div>
      </form>
    </>
  );
};
